import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  OnInit,
  signal,
  Signal,
  WritableSignal,
  OnDestroy
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import {
  MAT_BOTTOM_SHEET_DATA,
  MatBottomSheet,
  MatBottomSheetRef
} from '@angular/material/bottom-sheet';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  catchError,
  distinctUntilChanged,
  of,
  shareReplay, startWith,
  Subject,
  switchMap,
  tap
} from 'rxjs';
import {
  BeneficiaryNoPremium,
  Country,
  ExpatClient,
  ExpatPricing,
  PeriodicityPricing,
  PricingInformations
} from '../../core/api/models';
import { ClientGender } from '../../shared/helpers/client.utils';
import { AcsProjectModel } from '../../shared/model/acs-project.model';
import { CurrencyFormatPipe } from '../../shared/pipes/currency-format.pipe';
import { TranslatePipe } from '../../shared/pipes/translate.pipe';
import { SnackBarService } from '../../shared/services/snack-bar.service';
import { PriceAndFrequencyViewModel } from '../subscription-recap/subscription-recap.component';
import { TarifsResumeComponent } from './tarifs-resume/tarifs-resume.component';
import { DentalOpticalOptionSectionComponent } from './dental-optical-option-section/dental-optical-option-section.component';
import { MaternityOptionSectionComponent } from './maternity-option-section/maternity-option-section.component';
import { MedicalCareOptionSectionComponent } from './medical-care-option-section/medical-care-option-section.component';
import { HospitalisationEvacuationOptionSectionComponent } from './hospitalisation-evacuation-option-section/hospitalisation-evacuation-option-section.component';
import { ProductOptionSectionComponent } from './product-option-section/product-option-section.component';
import { AssistancePlusOptionSectionComponent } from './assistance-plus-option-section/assistance-plus-option-section.component';
import { DeductibleOptionSectionComponent } from './deductible-option-section/deductible-option-section.component';
import { PaymentFrequencyOptionSectionComponent } from './payment-frequency-option-section/payment-frequency-option-section.component';
import { CoverZoneOptionSectionComponent } from './cover-zone-option-section/cover-zone-option-section.component';
import { TarificationsComponentService } from '../../shared/services/component/tarifications-component.service';
import { StepperService } from '../../shared/services/stepper.service';
import { ReferenceStateService } from '../../shared/services/state/reference-state.service';

export interface RadioButtonLevel {
  level: 0 | 1 | 2 | 3 | null | undefined;
  disable: boolean;
  label: string;
}

interface RadioLevel {
  medicaux: RadioButtonLevel[];
  dentaire: RadioButtonLevel[];
  maternite: RadioButtonLevel[];

  [key: string]: RadioButtonLevel[];
}

export const INITIAL_VALUE_PRICING = {
  annual: 0,
  monthly: 0,
  quarterly: 0,
  semi_annual: 0
};

@Component({
  selector: 'app-acs-tarifications',
  imports: [
    MatRadioModule,
    FormsModule,
    ReactiveFormsModule,
    MatDividerModule,
    MatSelectModule,
    MatIconModule,
    MatTooltipModule,
    MatButtonModule,
    TranslatePipe,
    TarifsResumeComponent,
    CurrencyFormatPipe,
    DentalOpticalOptionSectionComponent,
    MaternityOptionSectionComponent,
    MedicalCareOptionSectionComponent,
    HospitalisationEvacuationOptionSectionComponent,
    ProductOptionSectionComponent,
    AssistancePlusOptionSectionComponent,
    DeductibleOptionSectionComponent,
    PaymentFrequencyOptionSectionComponent,
    CoverZoneOptionSectionComponent
  ],
  providers: [
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline', subscriptSizing: 'dynamic' }
    },
    { provide: MatBottomSheetRef, useValue: {} },
    { provide: MAT_BOTTOM_SHEET_DATA, useValue: {} },
    DatePipe,
    TranslatePipe
  ],
  templateUrl: './tarifications.component.html',
  styleUrl: './tarifications.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TarificationsComponent implements OnInit, OnDestroy {
  readonly #destroyRef = inject(DestroyRef);
  readonly #bottomSheet = inject(MatBottomSheet);
  readonly referenceStateService: ReferenceStateService = inject(
    ReferenceStateService
  );
  private tarificationsComponentService: TarificationsComponentService = inject(
    TarificationsComponentService
  );
  readonly #datePipe = inject(DatePipe);
  readonly #translatePipe = inject(TranslatePipe);
  readonly #snackBarService: SnackBarService = inject(SnackBarService);
  private stepperService: StepperService = inject(StepperService);
  readonly #acsProjectDataModel$ = this.tarificationsComponentService
    .getAcsProjectDataModel()
    .pipe(takeUntilDestroyed(), distinctUntilChanged(), shareReplay(1));
  readonly acsProjectDataSignal = toSignal(this.#acsProjectDataModel$);
  refreshSignal = signal(false);

  private destroy$: Subject<void> = new Subject();

  public isAuthorizedCfe = computed(() => {
    const currentLanguage =
      this.tarificationsComponentService.getCurrentLanguageCode();
    const natCountry =
      this.tarificationsComponentService.getPricingInformations()
        ?.expat_project_nationality;
    this.refreshSignal();
    if (!this.acsProjectDataSignal()?.acs_cfe_allowed_nationalities) {
      if (this.currencyCtrl.value === 'expat_cfe') {
        this.currencyCtrl.setValue('expat_eur', { emitEvent: false });
      }
      return false;
    }
    let isAutorized;
    if (currentLanguage === 'fr') {
      isAutorized =
        !!this.acsProjectDataSignal()?.acs_cfe_allowed_nationalities?.fr.find(
          (c) => c.alpha3 === natCountry
        );
    } else {
      isAutorized =
        !!this.acsProjectDataSignal()?.acs_cfe_allowed_nationalities?.en.find(
          (c) => c.alpha3 === natCountry
        );
    }
    if (!isAutorized && this.currencyCtrl.value === 'expat_cfe') {
      this.currencyCtrl.setValue('expat_eur', { emitEvent: false });
    }
    return isAutorized;
  });

  public ClientGender = ClientGender;

  public options: RadioLevel = {
    medicaux: [
      {
        level: 0,
        disable: false,
        label: 'tarificateur.option.selected'
      },
      {
        level: 1,
        disable: true,
        label: 'tarificateur.level1'
      },
      {
        level: 2,
        disable: true,
        label: 'tarificateur.level2'
      },
      {
        level: 3,
        disable: true,
        label: 'tarificateur.level3'
      }
    ],
    dentaire: [
      {
        level: 0,
        disable: false,
        label: 'tarificateur.option.selected'
      },
      {
        level: 1,
        disable: true,
        label: 'tarificateur.level1'
      },
      {
        level: 2,
        disable: true,
        label: 'tarificateur.level2'
      }
    ],
    maternite: [
      {
        level: 0,
        disable: false,
        label: 'tarificateur.option.selected'
      },
      {
        level: 1,
        disable: true,
        label: 'tarificateur.level1'
      },
      {
        level: 2,
        disable: true,
        label: 'tarificateur.level2'
      }
    ]
  };

  /**
   * Le contenu du select options pour l'option maternité
   * Si un conjoint existe, on le met dedans
   * Si le client est Mme alors on le met aussi.
   */
  public maternityOptionPopulate: {
    value: NonNullable<unknown>;
    label: string;
  }[] = [{ value: '0', label: 'tarificateur.option.selected' }];

  readonly #maternityOptionBeneficiaries: BeneficiaryNoPremium[] = [];

  public $isMaternityCompatible = computed(() => {
    return this.#isMaternityCompatible();
  });

  public $periodicityPricing: WritableSignal<PeriodicityPricing> = signal(
    INITIAL_VALUE_PRICING
  );
  public $expatPricing: Signal<ExpatPricing> =
    this.tarificationsComponentService.getExpatPricing();
  public $price: WritableSignal<PriceAndFrequencyViewModel> = signal({
    price: 0,
    frequency: ''
  });

  public form: FormGroup = new FormGroup({
    currency: new FormControl('expat_eur', Validators.required),
    expat_project_plan: new FormControl('Bronze', Validators.required),
    expat_project_medical_care_option: new FormControl(0, Validators.required),
    expat_project_dental_and_optical_option: new FormControl(
      0,
      Validators.required
    ),
    expat_project_maternity_option: new FormControl(0, Validators.required),
    beneficiaries: new FormControl(
      { value: '0', disabled: true },
      Validators.required
    ),
    expat_project_assistance_plus_option: new FormControl(
      0,
      Validators.required
    ),
    //Franchise
    expat_project_deductible: new FormControl(0),
    expat_project_periodicity_choice: new FormControl(4, Validators.required),
    //Second pays
    expat_project_additional_country: new FormControl(null),
    expat_project_cover_nationality_country: new FormControl(
      this.$expatPricing().already_covered_in_origin_country ?? 'Non'
    )
  });

  get currencyOptionFC() {
    return this.form.get('currency') as FormControl;
  }

  get hospitalisationEvacuationOptionFC() {
    return this.form.get('expat_project_plan') as FormControl;
  }

  get medicalCareOptionFC() {
    return this.form.get('expat_project_medical_care_option') as FormControl;
  }

  get dentalOptionFC() {
    return this.form.get(
      'expat_project_dental_and_optical_option'
    ) as FormControl;
  }

  get maternityOptionFC() {
    return this.form.get('expat_project_maternity_option') as FormControl;
  }

  get beneficiariesFC() {
    return this.form.get('beneficiaries') as FormControl;
  }

  get assistancePlusOptionFC() {
    return this.form.get('expat_project_assistance_plus_option') as FormControl;
  }

  get deductibleOptionFC() {
    return this.form.get('expat_project_deductible') as FormControl;
  }

  get periodicityPaymentOptionFC() {
    return this.form.get('expat_project_periodicity_choice') as FormControl;
  }

  get expatAdditionalCountryFC() {
    return this.form.get('expat_project_additional_country') as FormControl;
  }

  get expatCoverNationalityCountryFC() {
    return this.form.get(
      'expat_project_cover_nationality_country'
    ) as FormControl;
  }

  protected addCountries: Signal<Country[] | undefined> =
    this.referenceStateService.selectExpatriationCountries();

  private countryValue = toSignal(
    this.expatAdditionalCountryFC.valueChanges.pipe(
      startWith(this.expatAdditionalCountryFC.value ?? '')
    )
  );

  filteredCountries = computed(() => {
    const all = this.addCountries();
    const value = (this.countryValue() ?? '').toLowerCase();
    if (!value) return all;
    return all!.filter((c) => c.label.toLowerCase().includes(value));
  });

  private beneficiary: ExpatClient | null =
    this.tarificationsComponentService.getExpatClientInfos();

  public $coverageZone: Signal<string | undefined> = computed(() => {
    const currentLanguage =
      this.tarificationsComponentService.getCurrentLanguageCode();
    return currentLanguage === 'fr'
      ? this.tarificationsComponentService.getExpatPricing()().coverage_zone?.fr
      : this.tarificationsComponentService.getExpatPricing()().coverage_zone
          ?.en;
  });

  public $isLoading: Signal<boolean> =
    this.tarificationsComponentService.selectIsPriceLoading();

  constructor() {
    effect(() => {
      this.tarificationsComponentService.getCurrentLanguageCode();
      this.#populateMaternitySelect(
        +this.expatProjectMaternityOptionCtrl.value
      );
    });

    effect(() => {
      const countries =
        this.referenceStateService.selectExpatriationCountries()() ?? [];
      this.setAdditionalCountryValidator(countries);
    });

    effect(() => {
      this.addCountries()
      this.expatAdditionalCountryFC.setValue(this.tarificationsComponentService.getPricingInformations()?.expat_project_additional_country)
    });
  }

  private setAdditionalCountryValidator(countries: Country[]) {
    const mustBeFrom =
      (getList: () => Country[]) => (ctrl: AbstractControl) => {
        const v = ctrl.value as string | null;
        if (v == null || v === '') return null;
        const list = getList();
        return list.some((c) => c.alpha3 === v)
          ? null
          : { optionNotInList: true };
      };

    // Met à jour le validateur à chaque changement de langue / pays
    this.form
      .get('expat_project_additional_country')!
      .setValidators(mustBeFrom(() => countries));
  }

  public ngOnInit(): void {
    this.stepperService.step.set(2);
    this.#populateMaternityBeneficiaries();
    this.setAdditionalCountryValidator(
      this.referenceStateService.selectExpatriationCountries()() ?? []
    );
    /**
     * Vérification si on a des information stockées.
     */
    const tarificationProjectInfos =
      this.tarificationsComponentService.getPricingInformations();
    if (tarificationProjectInfos) {
      tarificationProjectInfos.beneficiaries =
        this.tarificationsComponentService.getBeneficiaryHasMaternityOption()!;
      this.form.patchValue(tarificationProjectInfos, { emitEvent: false });
      this.#setExpatProjectFieldsValidity(
        this.expatProjectPlanCtrl.value
      );
      this.#populateMaternitySelect(
        +this.expatProjectMaternityOptionCtrl.value
      );
      if (tarificationProjectInfos.expat_project_maternity_option === null) {
        this.expatProjectMaternityOptionCtrl.setValue(0);
      }
      if (this.expatProjectMaternityOptionCtrl.value === 0) {
        this.expatProjectMaternityOptionBeneficiaryCtrl.disable();
      }
      this.#expatProjectMedicalCareOptionFieldsValidity(
        tarificationProjectInfos.expat_project_medical_care_option
      );

      this.tarificationsComponentService.updatePricingInformations(
        this.form.value
      );
      if (!this.isAuthorizedCfe() && this.currencyCtrl.value === 'expat_cfe') {
        this.currencyCtrl.setValue('expat_eur');
      }
      this.tarificationsComponentService
        .postPricing(this.#formatData())
        .pipe(
          shareReplay(1),
          takeUntilDestroyed(this.#destroyRef),
          catchError((err) => {
            this.tarificationsComponentService.setIsPriceLoading(false);
            this.#snackBarService.errorMessage(err.error.message);
            this.form.setErrors({ invalid: true });
            this.$periodicityPricing.update(() => INITIAL_VALUE_PRICING);
            throw err;
          })
        )
        .subscribe((data) => {
          this.tarificationsComponentService.setExpatPricing(data);
          this.$periodicityPricing.update(() => data.periodicity_pricing);
          this.#setPriceAndFrequencyLabel(
            this.expatProjectPeriodicityChoice.value,
            data.periodicity_pricing
          );
          this.tarificationsComponentService.setIsPriceLoading(false);
        });
    }

    this.expatProjectPlanCtrl.valueChanges
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((value) => {
        this.#setExpatProjectFieldsValidity(value);
        this.#setExpatProjectFieldsValue(value);
      });

    this.expatProjectMedicalCareOptionsCtrl.valueChanges
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((value) => {
        this.#expatProjectMedicalCareOptionFieldsValidity(value);
      });

    this.expatProjectMaternityOptionCtrl.valueChanges
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((value) => {
        this.#populateMaternitySelect(+value);
      });

    /* ne fonctionne plus quand deplacé */

    this.form.valueChanges
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        tap((value) => {
          this.tarificationsComponentService.setBeneficiaryHasMaternityOption(
            this.expatProjectMaternityOptionBeneficiaryCtrl.value
          );
          this.tarificationsComponentService.updatePricingInformations(value);
          this.tarificationsComponentService.setIsPriceLoading(true);
        }),
        switchMap(() =>
          this.tarificationsComponentService
            .postPricing(this.#formatData())
            .pipe(
              catchError((err) => {
                this.tarificationsComponentService.setIsPriceLoading(false);
                this.form.setErrors({ invalid: true });
                this.#snackBarService.errorMessage(err.error.message);
                return of({
                  periodicity_pricing: INITIAL_VALUE_PRICING
                } as ExpatPricing);
              })
            )
        )
      )
      .subscribe((data) => {
        this.tarificationsComponentService.setIsPriceLoading(false);
        this.$periodicityPricing.update(() => data.periodicity_pricing);
        this.#setPriceAndFrequencyLabel(
          this.expatProjectPeriodicityChoice.value,
          data.periodicity_pricing
        );
        this.tarificationsComponentService.setExpatPricing(data);
      });
    this.initPricing();
  }

  public initPricing(): void {
    this.refreshSignal.set(!this.refreshSignal());
    this.tarificationsComponentService.setIsPriceLoading(true);
    if (!this.isAuthorizedCfe() && this.currencyCtrl.value === 'expat_cfe') {
      this.currencyCtrl.setValue('expat_eur');
    }
    const res = this.#formatData();
    this.tarificationsComponentService
      .postPricing(res)
      .pipe(
        catchError((err) => {
          this.tarificationsComponentService.setIsPriceLoading(false);
          this.#snackBarService.errorMessage(err.error.message);
          this.form.setErrors({ invalid: true });
          this.$periodicityPricing.update(() => INITIAL_VALUE_PRICING);
          throw err;
        })
      )
      .subscribe((data) => {
        this.form.setErrors(null);
        this.tarificationsComponentService.setExpatPricing(data);
        this.$periodicityPricing.update(() => data.periodicity_pricing);
        this.#setPriceAndFrequencyLabel(
          this.expatProjectPeriodicityChoice.value,
          data.periodicity_pricing
        );
        this.tarificationsComponentService.setIsPriceLoading(false);
      });
  }

  public setLevel(level: 0 | 1 | 2 | 3, enable: boolean): void {
    // Parcourir dynamiquement chaque catégorie de l'objet options
    Object.keys(this.options).forEach((category) => {
      // Mettre à jour la propriété disable de l'option correspondante
      const option = this.options[category].find((opt) => opt.level === level);
      if (option) {
        const isMedicalCategory = category === 'medicaux';
        const medicalCareValue = this.expatProjectMedicalCareOptionsCtrl.value;

        // Si ce n'est pas la catégorie 'medicaux' et que la valeur du contrôleur n'est pas 0
        if (!isMedicalCategory) {
          option.disable = medicalCareValue === 0 || !enable;
        } else {
          // Pour la catégorie 'medicaux'
          option.disable = !enable;
        }
      }
    });
    // Pour s'assurer que la detection de changement des composant s'active.
    // Dans l'idéal, il faudrait revoir cette propriété pour qu'elle reste immutable.
    this.options.dentaire = Object.assign([], this.options.dentaire);
    this.options.medicaux = Object.assign([], this.options.medicaux);
    this.options.maternite = Object.assign([], this.options.maternite);
  }

  public submit(): void {
    this.tarificationsComponentService.submitStep3(
      this.form,
      this.#formatData()
    );
  }

  public save(): void {
    this.tarificationsComponentService.saveStep3(this.#formatData(), this.form);
  }

  public openBottomSheet(): void {
    this.tarificationsComponentService.updatePricingInformations(
      this.form.value
    );
    this.#bottomSheet.open(TarifsResumeComponent);
  }

  #setExpatProjectFieldsValidity(value: string): void {
    switch (value) {
      case 'Bronze':
        this.setLevel(0, true);
        this.setLevel(1, false);
        this.setLevel(2, false);
        this.setLevel(3, false);
        break;
      case 'Silver':
        this.setLevel(0, true);
        this.setLevel(1, true);
        this.setLevel(2, false);
        this.setLevel(3, false);
        break;
      case 'Gold':
        this.setLevel(0, true);
        this.setLevel(1, true);
        this.setLevel(2, true);
        this.setLevel(3, false);
        break;
      case 'Platinum':
        this.setLevel(0, true);
        this.setLevel(1, true);
        this.setLevel(2, true);
        this.setLevel(3, true);
        break;
      default:
        break;
    }
  }

  #setExpatProjectFieldsValue(value: string): void {
    switch (value) {
      case 'Bronze':
        this.expatProjectMedicalCareOptionsCtrl.setValue(0, {
          emitEvent: false
        });
        this.expatProjectDentalAndOpticalOptionCtrl.setValue(0, {
          emitEvent: false
        });
        if (this.#isMaternityCompatible()) {
          this.expatProjectMaternityOptionCtrl.setValue(0, {
            emitEvent: false
          });
        }
        break;
      case 'Silver':
        if (this.expatProjectMedicalCareOptionsCtrl.value > 1) {
          this.expatProjectMedicalCareOptionsCtrl.setValue(1, {
            emitEvent: false
          });
        }
        if (this.expatProjectDentalAndOpticalOptionCtrl.value > 1) {
          this.expatProjectDentalAndOpticalOptionCtrl.setValue(1, {
            emitEvent: false
          });
        }
        if (
          this.#isMaternityCompatible() &&
          this.expatProjectMaternityOptionCtrl.value > 1
        ) {
          this.expatProjectMaternityOptionCtrl.setValue(1, {
            emitEvent: false
          });
        }
        break;
      case 'Gold':
        if (this.expatProjectMedicalCareOptionsCtrl.value > 2) {
          this.expatProjectMedicalCareOptionsCtrl.setValue(2, {
            emitEvent: false
          });
        }
        if (this.expatProjectDentalAndOpticalOptionCtrl.value > 2) {
          this.expatProjectDentalAndOpticalOptionCtrl.setValue(2, {
            emitEvent: false
          });
        }
        if (
          this.#isMaternityCompatible() &&
          this.expatProjectMaternityOptionCtrl.value > 2
        ) {
          this.expatProjectMaternityOptionCtrl.setValue(2, {
            emitEvent: false
          });
        }
        break;
      case 'Platinum':
        break;
      default:
        break;
    }
  }

  #setPriceAndFrequencyLabel(
    periodicity: number,
    periodicity_pricing: PeriodicityPricing
  ): void {
    switch (periodicity) {
      case 1:
        this.$price.set({
          price: periodicity_pricing.annual,
          frequency: 'subscription.resume.frequency.yearly'
        });
        break;
      case 2:
        this.$price.set({
          price: periodicity_pricing.semi_annual,
          frequency: 'subscription.resume.frequency.sixmonths'
        });

        break;
      case 3:
        this.$price.set({
          price: periodicity_pricing.quarterly,
          frequency: 'subscription.resume.frequency.threemonths'
        });
        break;
      case 4:
        this.$price.set({
          price: periodicity_pricing.monthly,
          frequency: 'subscription.resume.frequency.monthly'
        });
        break;
    }
  }

  #expatProjectMedicalCareOptionFieldsValidity(
    value: number | null | undefined
  ): void {
    if (value === null || value === undefined) {
      return;
    }
    if (value === 0 && this.expatProjectPlanCtrl.value !== 'Bronze') {
      this.expatProjectDentalAndOpticalOptionCtrl.setValue(0);
      this.expatProjectMaternityOptionCtrl.setValue(0);
      this.setLevel(1, false);
      this.setLevel(2, false);
      this.setLevel(3, false);
      this.options.medicaux.find((opt) => opt.level === 1)!.disable = false;
      if (this.expatProjectPlanCtrl.value == 'Gold') {
        this.options.medicaux.find((opt) => opt.level === 2)!.disable = false;
      }
      if (this.expatProjectPlanCtrl.value == 'Platinum') {
        this.options.medicaux.find((opt) => opt.level === 2)!.disable = false;
        this.options.medicaux.find((opt) => opt.level === 3)!.disable = false;
      }
      this.expatProjectMaternityOptionBeneficiaryCtrl.disable();
    } else {
      this.#setExpatProjectFieldsValidity(this.expatProjectPlanCtrl.value);
    }
  }

  #isMaternityCompatible(): boolean {
    return (
      this.beneficiary!.title === this.ClientGender.FEMALE ||
      this.tarificationsComponentService
        .getProjectBeneficiaries()
        .filter((c) => c.category === 'Conjoint').length > 0
    );
  }

  #populateMaternitySelect(value: number): void {
    if (value !== 0) {
      this.expatProjectMaternityOptionBeneficiaryCtrl.enable({
        emitEvent: false
      });
      this.maternityOptionPopulate = [
        {
          value: 'Souscripteur',
          label: this.#translatePipe.transform(
            'tarificateur.subscriptor',
            this.beneficiary!.last_name,
            this.beneficiary!.first_name
          )
        }
      ];
      const conjointe = this.tarificationsComponentService
        .getProjectBeneficiaries()
        .filter((c) => c.category === 'Conjoint');
      if (conjointe.length > 0) {
        const formattedDate = this.#datePipe.transform(
          conjointe[0].birth_date,
          'dd/MM/yyyy'
        );
        this.maternityOptionPopulate.push({
          value: 'Conjoint',
          label: this.#translatePipe.transform(
            'tarificateur.spouse.born',
            formattedDate!
          )
        });
      }
    } else {
      this.expatProjectMaternityOptionBeneficiaryCtrl.disable({
        emitEvent: false
      });
      this.maternityOptionPopulate = [
        {
          value: '0',
          label: this.#translatePipe.transform('tarificateur.option.selected')
        }
      ];
      this.expatProjectMaternityOptionBeneficiaryCtrl.setValue('0', {
        emitEvent: false
      });
    }
  }

  #populateMaternityBeneficiaries(): void {
    const sub = this.tarificationsComponentService
      .getProjectBeneficiaries()
      .find((b) => b.category === 'Souscripteur');
    this.#maternityOptionBeneficiaries.push({
      birth_date: this.beneficiary!.birth_date,
      category: 'Souscripteur',
      acs_expat_beneficiary_maternity_option: 0,
      acs_expat_beneficiary_business_sector:
        sub?.acs_expat_beneficiary_business_sector ?? 0,
      acs_expat_beneficiary_email: this.beneficiary!.email,
      acs_expat_beneficiary_nationality_country: this.beneficiary!.country!,
      acs_expat_beneficiary_occupation:
        sub?.acs_expat_beneficiary_occupation ?? '',
      acs_expat_beneficiary_ppe: false,
      first_name: this.beneficiary!.first_name,
      last_name: this.beneficiary!.last_name
    });

    const conjointe = this.tarificationsComponentService
      .getProjectBeneficiaries()
      .filter((c) => c.category === 'Conjoint');
    if (conjointe.length > 0) {
      this.#maternityOptionBeneficiaries.push({
        birth_date: conjointe[0].birth_date,
        category: 'Conjoint',
        acs_expat_beneficiary_maternity_option: 0,
        acs_expat_beneficiary_business_sector:
          conjointe[0].acs_expat_beneficiary_business_sector,
        acs_expat_beneficiary_email: conjointe[0].acs_expat_beneficiary_email,
        acs_expat_beneficiary_nationality_country:
          conjointe[0].acs_expat_beneficiary_nationality_country,
        acs_expat_beneficiary_occupation:
          conjointe[0].acs_expat_beneficiary_occupation,
        acs_expat_beneficiary_ppe: conjointe[0].acs_expat_beneficiary_ppe,
        first_name: conjointe[0].first_name,
        last_name: conjointe[0].last_name
      });
    }
  }

  #formatData(): AcsProjectModel {
    const project: PricingInformations & {
      beneficiaries?: NonNullable<unknown>;
      currency?: string;
    } = this.tarificationsComponentService.getPricingInformations()!;
    delete project.beneficiaries;
    delete project.currency;
    if (project.expat_project_additional_country === '0') {
      delete project.expat_project_additional_country;
    }
    if (
      project.expat_project_deductible &&
      project.expat_project_deductible.toString() === '0'
    ) {
      project.expat_project_deductible = +project.expat_project_deductible;
    }
    if (
      project.expat_project_maternity_option &&
      project.expat_project_maternity_option?.toString() === '0'
    ) {
      delete project.expat_project_maternity_option;
    }
    project.expat_project_deductible = +project.expat_project_deductible;
    if (
      this.$expatPricing().already_covered_in_origin_country === 'Oui' ||
      this.$expatPricing().already_covered_in_origin_country ===
        'non_applicable'
    ) {
      project.expat_project_cover_nationality_country = 'Non';
    } else {
      project.expat_project_cover_nationality_country =
        this.tarificationsComponentService.getPricingInformations()!.expat_project_cover_nationality_country;
    }
    let beneficiaries = this.#maternityOptionBeneficiaries;
    const childrens = this.tarificationsComponentService
      .getProjectBeneficiaries()
      .filter((b) => b.category === 'Enfant') as BeneficiaryNoPremium[];
    beneficiaries = beneficiaries.concat(childrens);
    beneficiaries.forEach(
      (item) =>
        (item.acs_expat_beneficiary_maternity_option =
          item.category ===
          this.expatProjectMaternityOptionBeneficiaryCtrl.value
            ? 1
            : 0)
    );

    const client = this.tarificationsComponentService.getExpatClientInfos()!;
    const clientToSend: ExpatClient = {
      address_1: client.address_1,
      address_2: client.address_2,
      birth_date: client.birth_date,
      city: client.city,
      country: client.country,
      email: client.email,
      first_name: client.first_name,
      language: client.language,
      last_name: client.last_name,
      phone: client.phone,
      postal_code: client.postal_code,
      terms_accepted: client.terms_accepted,
      data_consent_commercial: client.data_consent_commercial,
      title: client.title
    };
    this.tarificationsComponentService.updateExpatClientInfos(
      {
        ...clientToSend,
        beneficiaries: [...beneficiaries]
      },
      true
    );

    return {
      pricing: project,
      beneficiaries: beneficiaries,
      product: this.currencyCtrl.value
    };
  }

  /** Getter for form */
  get expatProjectPlanCtrl(): FormControl {
    return this.form.get('expat_project_plan') as FormControl;
  }

  get expatProjectMedicalCareOptionsCtrl(): FormControl {
    return this.form.get('expat_project_medical_care_option') as FormControl;
  }

  get expatProjectDentalAndOpticalOptionCtrl(): FormControl {
    return this.form.get(
      'expat_project_dental_and_optical_option'
    ) as FormControl;
  }

  get expatProjectMaternityOptionCtrl(): FormControl {
    return this.form.get('expat_project_maternity_option') as FormControl;
  }

  get expatProjectMaternityOptionBeneficiaryCtrl(): FormControl {
    return this.form.get('beneficiaries') as FormControl;
  }

  get currencyCtrl(): FormControl {
    return this.form.get('currency') as FormControl;
  }

  get expatProjectPeriodicityChoice(): FormControl {
    return this.form.get('expat_project_periodicity_choice') as FormControl;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }
}
